import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Edit2,
  Trash2,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Calendar,
  Clock,
  CheckCircle,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadEvenementsFromDB = async (entrepriseId) => {
  const { data, error } = await supabase
    .from("evenements")
    .select("*")
    .eq("entreprise_id", entrepriseId)
    .order("date_debut", { ascending: true });

  if (error) throw error;
  return data || [];
};

const addEvenementToDB = async (item) => {
  const { data, error } = await supabase
    .from("evenements")
    .insert([item])
    .select()
    .single();

  if (error) throw error;
  return data;
};

const updateEvenementInDB = async (id, updates) => {
  const { data, error } = await supabase
    .from("evenements")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const deleteEvenementFromDB = async (id) => {
  const { error } = await supabase
    .from("evenements")
    .delete()
    .eq("id", id);

  if (error) throw error;
  return true;
};

// ============================================
// UTILITAIRES
// ============================================

const MOIS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

const JOURS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

const TYPES_EVENEMENT = [
  { id: "echeance", label: "Échéance", couleur: "#ef4444" },
  { id: "reunion", label: "Réunion", couleur: "#3b82f6" },
  { id: "audit", label: "Audit", couleur: "#8b5cf6" },
  { id: "formation", label: "Formation", couleur: "#10b981" },
  { id: "rappel", label: "Rappel", couleur: "#f59e0b" },
  { id: "autre", label: "Autre", couleur: "#6b7280" },
];

const formatDate = (date) => {
  return date.toISOString().split("T")[0];
};

const isSameDay = (date1, date2) => {
  return formatDate(date1) === date2;
};

const isToday = (dateStr) => {
  return dateStr === formatDate(new Date());
};

const isPast = (dateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr) < today;
};

// ============================================
// COMPOSANTS UI
// ============================================

const LoadingSpinner = ({ message = "Chargement..." }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <Loader2 size={40} className="text-sky-600 animate-spin mb-4" />
    <p className="text-gray-500">{message}</p>
  </div>
);

const ErrorMessage = ({ message, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
    <AlertTriangle size={40} className="text-red-500 mx-auto mb-4" />
    <p className="text-red-700 font-medium mb-4">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 inline-flex items-center gap-2"
      >
        <RefreshCw size={16} /> Réessayer
      </button>
    )}
  </div>
);

// ============================================
// MODAL D'AJOUT/ÉDITION
// ============================================

const AddEditModal = ({ item, selectedDate, onClose, onSave, isLoading }) => {
  const [form, setForm] = useState(
    item || {
      titre: "",
      description: "",
      date_debut: selectedDate || formatDate(new Date()),
      date_fin: "",
      type: "echeance",
      couleur: "#ef4444",
      rappel: false,
      completed: false,
    }
  );

  const handleTypeChange = (typeId) => {
    const type = TYPES_EVENEMENT.find((t) => t.id === typeId);
    setForm({ ...form, type: typeId, couleur: type?.couleur || "#6b7280" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form, item?.id);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-slate-800 dark:text-white">
            {item ? "Modifier l'événement" : "Nouvel événement"}
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-2 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titre *
            </label>
            <input
              type="text"
              required
              value={form.titre}
              onChange={(e) => setForm({ ...form, titre: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2"
              placeholder="Ex: Révision politique de confidentialité"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2"
              rows={2}
              placeholder="Détails..."
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date début *
              </label>
              <input
                type="date"
                required
                value={form.date_debut}
                onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date fin
              </label>
              <input
                type="date"
                value={form.date_fin || ""}
                onChange={(e) => setForm({ ...form, date_fin: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2"
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type d'événement
            </label>
            <div className="grid grid-cols-3 gap-2">
              {TYPES_EVENEMENT.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleTypeChange(type.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                    form.type === type.id
                      ? "border-current"
                      : "border-transparent bg-gray-100 dark:bg-gray-700"
                  }`}
                  style={{
                    backgroundColor: form.type === type.id ? `${type.couleur}20` : undefined,
                    color: form.type === type.id ? type.couleur : undefined,
                  }}
                  disabled={isLoading}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.rappel}
                onChange={(e) => setForm({ ...form, rappel: e.target.checked })}
                className="rounded text-sky-600"
                disabled={isLoading}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Rappel</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.completed}
                onChange={(e) => setForm({ ...form, completed: e.target.checked })}
                className="rounded text-green-600"
                disabled={isLoading}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Complété</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-slate-100 dark:bg-gray-600 text-slate-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-slate-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-700 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Enregistrement...
                </>
              ) : item ? (
                "Enregistrer"
              ) : (
                "Ajouter"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const CalendarViewSupabase = ({ entrepriseId }) => {
  const [evenements, setEvenements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState("month"); // month, list

  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await loadEvenementsFromDB(entrepriseId);
      setEvenements(data);
    } catch (err) {
      setError("Erreur lors du chargement des événements.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation du calendrier
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Générer les jours du mois
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const days = [];

    // Jours vides au début
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, date: null });
    }

    // Jours du mois
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDate(date);
      const dayEvents = evenements.filter((e) => e.date_debut === dateStr);
      days.push({ day, date: dateStr, events: dayEvents });
    }

    return days;
  };

  // Handlers
  const handleSave = async (formData, existingId) => {
    setIsSaving(true);
    try {
      if (existingId) {
        const updated = await updateEvenementInDB(existingId, formData);
        setEvenements(evenements.map((e) => (e.id === existingId ? updated : e)));
      } else {
        const created = await addEvenementToDB({ ...formData, entreprise_id: entrepriseId });
        setEvenements([...evenements, created]);
      }
      setShowAddModal(false);
      setEditingItem(null);
      setSelectedDate(null);
    } catch (err) {
      alert("Erreur lors de l'enregistrement.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try {
      await deleteEvenementFromDB(id);
      setEvenements(evenements.filter((e) => e.id !== id));
    } catch (err) {
      alert("Erreur lors de la suppression.");
      console.error(err);
    }
  };

  const handleToggleComplete = async (item) => {
    try {
      const updated = await updateEvenementInDB(item.id, { completed: !item.completed });
      setEvenements(evenements.map((e) => (e.id === item.id ? updated : e)));
    } catch (err) {
      alert("Erreur lors de la mise à jour.");
      console.error(err);
    }
  };

  const handleDayClick = (dateStr) => {
    setSelectedDate(dateStr);
    setEditingItem(null);
    setShowAddModal(true);
  };

  // Filtres pour la vue liste
  const upcomingEvents = evenements
    .filter((e) => !isPast(e.date_debut) || isToday(e.date_debut))
    .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));

  const pastEvents = evenements
    .filter((e) => isPast(e.date_debut) && !isToday(e.date_debut))
    .sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut));

  if (!entrepriseId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-amber-500 mx-auto mb-4" />
        <p className="text-amber-700">Veuillez sélectionner une entreprise.</p>
      </div>
    );
  }

  if (isLoading) return <LoadingSpinner message="Chargement du calendrier..." />;
  if (error) return <ErrorMessage message={error} onRetry={loadData} />;

  const calendarDays = generateCalendarDays();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Calendrier</h2>
          <p className="text-gray-500 dark:text-gray-400">Gérez vos échéances et événements</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode(viewMode === "month" ? "list" : "month")}
            className="bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 px-3 py-2 rounded-lg hover:bg-slate-200"
          >
            {viewMode === "month" ? "Vue liste" : "Vue mois"}
          </button>
          <button
            onClick={loadData}
            className="bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 px-3 py-2 rounded-lg hover:bg-slate-200"
          >
            <RefreshCw size={20} />
          </button>
          <button
            onClick={() => {
              setSelectedDate(formatDate(new Date()));
              setEditingItem(null);
              setShowAddModal(true);
            }}
            className="bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Nouvel événement</span>
          </button>
        </div>
      </div>

      {viewMode === "month" ? (
        /* VUE CALENDRIER MOIS */
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          {/* Navigation du mois */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={prevMonth}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-4">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                {MOIS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h3>
              <button
                onClick={goToToday}
                className="text-sm text-sky-600 hover:text-sky-700 font-medium"
              >
                Aujourd'hui
              </button>
            </div>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Jours de la semaine */}
          <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-700">
            {JOURS.map((jour) => (
              <div
                key={jour}
                className="py-2 text-center text-sm font-medium text-gray-600 dark:text-gray-300"
              >
                {jour}
              </div>
            ))}
          </div>

          {/* Grille du calendrier */}
          <div className="grid grid-cols-7">
            {calendarDays.map((dayInfo, index) => (
              <div
                key={index}
                onClick={() => dayInfo.date && handleDayClick(dayInfo.date)}
                className={`min-h-[100px] p-2 border-t border-r border-gray-200 dark:border-gray-700 cursor-pointer transition-colors ${
                  dayInfo.day
                    ? isToday(dayInfo.date)
                      ? "bg-sky-50 dark:bg-sky-900/20"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    : "bg-gray-50 dark:bg-gray-800"
                } ${index % 7 === 0 ? "border-l" : ""}`}
              >
                {dayInfo.day && (
                  <>
                    <div
                      className={`text-sm font-medium mb-1 ${
                        isToday(dayInfo.date)
                          ? "text-sky-600 dark:text-sky-400"
                          : "text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {dayInfo.day}
                    </div>
                    <div className="space-y-1">
                      {dayInfo.events?.slice(0, 3).map((event) => (
                        <div
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingItem(event);
                            setShowAddModal(true);
                          }}
                          className={`text-xs px-2 py-1 rounded truncate ${
                            event.completed ? "opacity-50 line-through" : ""
                          }`}
                          style={{
                            backgroundColor: `${event.couleur}20`,
                            color: event.couleur,
                          }}
                        >
                          {event.titre}
                        </div>
                      ))}
                      {dayInfo.events?.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          +{dayInfo.events.length - 3} autres
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* VUE LISTE */
        <div className="space-y-6">
          {/* Événements à venir */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-sky-600" />
              À venir ({upcomingEvents.length})
            </h3>
            {upcomingEvents.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                Aucun événement à venir
              </p>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <div
                    key={event.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-l-4 ${
                      event.completed ? "opacity-50" : ""
                    }`}
                    style={{
                      borderColor: event.couleur,
                      backgroundColor: `${event.couleur}10`,
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => handleToggleComplete(event)}
                        className={`p-1 rounded-full ${
                          event.completed
                            ? "text-green-500 bg-green-50"
                            : "text-gray-300 hover:text-green-500"
                        }`}
                      >
                        <CheckCircle size={20} />
                      </button>
                      <div>
                        <p
                          className={`font-medium text-gray-900 dark:text-white ${
                            event.completed ? "line-through" : ""
                          }`}
                        >
                          {event.titre}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock size={14} />
                          <span>{event.date_debut}</span>
                          {event.rappel && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                              Rappel
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingItem(event);
                          setShowAddModal(true);
                        }}
                        className="p-2 hover:bg-white dark:hover:bg-gray-700 rounded-lg"
                      >
                        <Edit2 size={16} className="text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Événements passés */}
          {pastEvents.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-500 dark:text-gray-400 mb-4">
                Passés ({pastEvents.length})
              </h3>
              <div className="space-y-2">
                {pastEvents.slice(0, 5).map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg opacity-60"
                  >
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300 line-through">
                        {event.titre}
                      </p>
                      <p className="text-sm text-gray-500">{event.date_debut}</p>
                    </div>
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                      <Trash2 size={16} className="text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow text-center">
          <p className="text-2xl font-bold text-slate-800 dark:text-white">{evenements.length}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-sky-50 dark:bg-sky-900/20 p-4 rounded-lg shadow text-center">
          <p className="text-2xl font-bold text-sky-600">{upcomingEvents.length}</p>
          <p className="text-sm text-sky-600">À venir</p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg shadow text-center">
          <p className="text-2xl font-bold text-green-600">
            {evenements.filter((e) => e.completed).length}
          </p>
          <p className="text-sm text-green-600">Complétés</p>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg shadow text-center">
          <p className="text-2xl font-bold text-amber-600">
            {evenements.filter((e) => e.rappel && !e.completed).length}
          </p>
          <p className="text-sm text-amber-600">Rappels</p>
        </div>
      </div>

      {/* Modal */}
      {showAddModal && (
        <AddEditModal
          item={editingItem}
          selectedDate={selectedDate}
          onClose={() => {
            setShowAddModal(false);
            setEditingItem(null);
            setSelectedDate(null);
          }}
          onSave={handleSave}
          isLoading={isSaving}
        />
      )}
    </div>
  );
};

export default CalendarViewSupabase;